import { Link, useParams } from 'react-router-dom';
import usePageMeta from '../lib/usePageMeta';
import PageHero from '../components/PageHero';
import Icon from '../components/Icon';
import ProjectCard from '../components/ProjectCard';
import { SectionHead, FaqList } from '../components/Blocks';
import { SERVICES, SERVICE_BY_SLUG, PROJECTS, PROCESS } from '../data';
import NotFound from './NotFound';

export default function ServiceDetail() {
  const { slug } = useParams();
  const s = SERVICE_BY_SLUG[slug];
  usePageMeta(s ? s.title : 'Not found', s ? `${s.title} by Origo Private Limited. ${s.tagline} ${s.desc}` : '');
  if (!s) return <NotFound />;

  const related = PROJECTS.filter((p) => p.services.includes(s.slug)).slice(0, 3);
  const others = SERVICES.filter((x) => x.slug !== s.slug).slice(0, 4);

  return (
    <>
      <PageHero
        eyebrow={s.title}
        title={s.tagline}
        description={s.desc}
        crumbs={[{ label: 'Services', to: '/services' }, { label: s.title }]}
      >
        <div className="flex flex-wrap gap-4">
          <Link to="/contact" className="btn-primary">Get a free quote <Icon name="arrow" className="h-4 w-4" /></Link>
          <Link to="/portfolio" className="btn-ghost">See our work</Link>
        </div>
      </PageHero>

      <section className="section bg-white">
        <div className="container-x grid gap-14 lg:grid-cols-[1.2fr_.8fr]">
          <div data-anim="left">
            <span className="eyebrow">Overview</span>
            <h2 className="h2 mt-5 text-ink">{s.title} that moves your <span className="text-accent">business</span> <span className="text-outline">forward</span></h2>
            <div className="mt-6 space-y-5 text-lg leading-relaxed text-slate-600">
              {s.overview.map((p) => <p key={p}>{p}</p>)}
            </div>
          </div>
          <aside className="space-y-6" data-anim="right">
            <div className="rounded-3xl bg-gradient-to-br from-brand to-ink p-8 text-white">
              <span className="grid h-14 w-14 place-items-center rounded-2xl bg-brand-lime/20 text-brand-lime"><Icon name={s.icon} className="h-7 w-7" /></span>
              <h3 className="mt-5 text-xl font-bold">What you get</h3>
              <ul className="mt-4 space-y-3">
                {s.deliverables.map((d) => <li key={d} className="flex gap-2 text-white/85"><Icon name="check" className="mt-0.5 h-5 w-5 shrink-0 text-brand-lime" strokeWidth={2.6} />{d}</li>)}
              </ul>
            </div>
            <div className="rounded-3xl border border-brand/10 bg-brand-soft/60 p-8">
              <h3 className="text-lg font-bold text-ink">Tools & technologies</h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {s.tech.map((t) => <span key={t} className="rounded-full bg-white px-3.5 py-1.5 text-sm font-medium text-brand shadow-sm">{t}</span>)}
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section className="section bg-brand-soft">
        <div className="container-x">
          <SectionHead eyebrow="What is included" title={`${s.title} capabilities`} text="A closer look at what our team can do for you." />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-anim="up" data-stagger>
            {s.features.map((f, i) => (
              <div key={f.title} className="lift rounded-3xl bg-white p-7 shadow-sm ring-1 ring-brand/10">
                <span className="font-display text-3xl font-bold text-brand">{String(i + 1).padStart(2, '0')}</span>
                <h3 className="mt-3 text-lg font-bold text-ink">{f.title}</h3>
                <p className="mt-2 leading-relaxed text-slate-600">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-ink text-white">
        <div className="grid-bg pointer-events-none absolute inset-0" />
        <div className="container-x relative">
          <SectionHead dark eyebrow="How it works" title="From first call to launch" />
          <ol className="mt-14 grid gap-5 md:grid-cols-5" data-anim="up" data-stagger>
            {PROCESS.map((p) => (
              <li key={p.n} className="rounded-3xl border border-white/10 bg-white/[0.04] p-6">
                <span className="font-display text-3xl font-bold text-brand-lime">{p.n}</span>
                <h3 className="mt-3 text-lg font-bold">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/65">{p.desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {related.length > 0 && (
        <section className="section bg-white">
          <div className="container-x">
            <SectionHead eyebrow="Related work" title={`${s.title} projects`} />
            <div className="mt-12 grid gap-7 sm:grid-cols-2 lg:grid-cols-3" data-anim="zoom" data-stagger>
              {related.map((p, i) => <ProjectCard key={p.slug} project={p} index={i} />)}
            </div>
          </div>
        </section>
      )}

      <section className="section bg-brand-soft">
        <div className="container-x grid gap-14 lg:grid-cols-[.8fr_1.2fr]">
          <div data-anim="left">
            <span className="eyebrow">FAQ</span>
            <h2 className="h2 mt-5 text-ink">{s.title}: common questions</h2>
            <Link to="/contact" className="btn-green mt-8">Ask our team <Icon name="arrow" className="h-4 w-4" /></Link>
          </div>
          <FaqList items={s.faqs} />
        </div>
      </section>

      <section className="section bg-white">
        <div className="container-x">
          <SectionHead eyebrow="Explore more" title="Other services you may need" />
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-anim="up" data-stagger>
            {others.map((o) => (
              <Link key={o.slug} to={`/services/${o.slug}`} className="svc-card lift group relative rounded-3xl border border-brand/10 bg-white p-6 shadow-sm">
                <span className="svc-ico grid h-12 w-12 place-items-center rounded-xl bg-brand-soft text-brand transition-all duration-300"><Icon name={o.icon} /></span>
                <h3 className="mt-4 text-lg font-bold text-ink">{o.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{o.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
